import {
  ContextualMenu,
  DefaultButton,
  IDragOptions,
  Modal,
  Stack,
  Text,
  TooltipHost,
  useTheme,
} from '@fluentui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { CloseButton } from 'common/components/Buttons';
import {
  FormHookAmount,
  FormHookTextField,
} from 'common/components/FormHooksFields';
import { FormHookCheckBox } from 'common/components/FormHooksFields/FormHookCheckBox';
import { useCommonStyles } from 'common/styles';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { APPROVAL_LEVELS_INITIAL_VALUES } from 'settings/account/intuitiveApprovalSetupFlow/ApprovalFlowDesigner/constants';
import {
  ApprovalLevelViewData,
  ApprovalLevelViewRowValues,
} from 'settings/account/intuitiveApprovalSetupFlow/ApprovalFlowDesigner/types';
import { validationFormSchema } from './validation';
const dragOptions: IDragOptions = {
  moveMenuItemText: 'Move',
  closeMenuItemText: 'Close',
  menu: ContextualMenu,
  dragHandleSelector: '.ms-Modal-scrollableContent > div:first-child',
};

const REQUIRED_APPROVERS_TOOLTIP =
  'This number represents the number of approvers required for this level to be approved.';

const APPROVED_BY_CONSENSUS_TOOLTIP =
  'If checked, the level is approved when the number of users who approve the transaction matches the number of required approvers, regardless of how many approvers are active at this level.(Leave unchecked if all approvers at this level must approve for the level to be approved)';
const ADDITIONAL_DEPT_LEVEL_TOOLTIP1 =
  "If checked, this level will be used exclusively for additional business units or departments that are different from the transaction's primary business unit or department.";
const ADDITIONAL_DEPT_LEVEL_TOOLTIP2 =
  '(Note: Only one level can have the "Additional Department" designation.)';
const SINGLE_DECLINE_TOOLTIP =
  'If checked, and “Approved By Consensus?” is also selected, the approval is immediately declined if any approver rejects the transaction.(Leave unchecked if some approvers are allowed to decline, as long as the remaining approvers can satisfy the required number of approvers)';
const MINIMUM_APPROVERS_TOOLTIP =
  'If checked, and there are not enough approvers to satisfy the Required Approvers value, the requester will receive an error message stating that there are not enough approvers to process the request.(Leave unchecked if you’d like to bypass this approval level when the total number of approvers is not met)';

export const FormView: React.FC<{
  data?: ApprovalLevelViewData;
  onClose: () => void;
  onSave: (formFields: ApprovalLevelViewRowValues) => void;
  visibility?: boolean;
}> = ({ onClose, onSave, data, visibility }) => {
  const theme = useTheme();
  const isNew = data === undefined;
  const formMethods = useForm<ApprovalLevelViewRowValues>({
    mode: 'all',
    resolver: yupResolver(validationFormSchema),
  });

  const commonStyles = useCommonStyles();

  const tooltipContent = () => {
    return (
      <Stack>
        <Text>{ADDITIONAL_DEPT_LEVEL_TOOLTIP1}</Text>
        <Text className={commonStyles.bold}>
          {ADDITIONAL_DEPT_LEVEL_TOOLTIP2}
        </Text>
      </Stack>
    );
  };
  const {
    trigger,
    reset,
    handleSubmit,
    formState: { isDirty, isValid },
  } = { ...formMethods };

  const onHandleSave = async (values: ApprovalLevelViewRowValues) => {
    onSave(values);
  };

  useEffect(() => {
    trigger();
    reset(
      !isNew
        ? ({
            ...data,
            requiredBranchApprovalCount:
              data.requiredBranchApprovalCount?.toString(),
          } as ApprovalLevelViewRowValues)
        : APPROVAL_LEVELS_INITIAL_VALUES
    );
    // }
  }, [data, reset, trigger, isNew, visibility]);

  return (
    <FormProvider {...formMethods}>
      {visibility && (
        <Modal
          isModeless={!isValid && !isNew}
          onDismiss={() => {
            if (isDirty && isValid) {
              handleSubmit(onHandleSave)();
            } else onClose();
          }}
          isOpen
          dragOptions={dragOptions}
          styles={{
            scrollableContent: {
              overflow: 'scroll',
              '::-webkit-scrollbar': {
                width: 5,
              },
              '::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.neutralLight,
                borderRadius: '6px',
                width: 5,
              },
              '::-webkit-scrollbar-thumb:hover': {
                backgroundColor: theme.palette.neutralLighter,
              },
            },
          }}
        >
          <Stack
            horizontal
            grow={1}
            style={{
              position: 'fixed',
              backgroundColor: `${theme.palette.neutralLighterAlt}`,
              width: '100%',
              zIndex: 10,
              maxHeight: 600,
            }}
            horizontalAlign="space-between"
            tokens={{ padding: '20px 20px 20px 20px' }}
          >
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 7 }}
            >
              <Text variant={'xLarge'}>{`${isNew ? 'Add' : 'Edit'}`}</Text>

              <Text
                variant="xLarge"
                className={clsx(
                  commonStyles.colorThemePrimary,
                  commonStyles.bold
                )}
              >
                Level {isNew ? '' : ` - ${data?.name}`}
              </Text>
            </Stack>
            <CloseButton
              onClick={() => {
                if (isDirty && isValid) {
                  handleSubmit(onHandleSave)();
                } else onClose();
              }}
              disabled={!isValid && !isNew}
            />
          </Stack>

          {/* Main Form */}
          <Stack tokens={{ childrenGap: 20, padding: '100px 20px' }}>
            {/* FORM HOOK TEXT DESCRIPTION */}
            <Stack horizontal tokens={{ childrenGap: 20 }}>
              <Stack grow={2}>
                <FormHookTextField
                  label="Level Name"
                  name={`name`}
                  placeholder="Enter name"
                  disabled={data?.isFlowUpdatable === false}
                />
              </Stack>
              <Stack grow={1}>
                <TooltipHost
                  content={REQUIRED_APPROVERS_TOOLTIP}
                  id="requiredApprovers"
                >
                  <FormHookAmount
                    label="Required Approvers"
                    name={`requiredBranchApprovalCount`}
                    placeholder="Enter # of approvers"
                    disabled={data?.isFlowUpdatable === false}
                  />
                </TooltipHost>
              </Stack>
            </Stack>

            <FormHookTextField
              label="Comment"
              multiline
              name={`description`}
              placeholder="Enter comment"
              disabled={data?.isFlowUpdatable === false}
            />

            <Stack tokens={{ childrenGap: 10, padding: '20px 0px' }}>
              <Stack horizontal grow tokens={{ childrenGap: 20 }}>
                <TooltipHost
                  content={APPROVED_BY_CONSENSUS_TOOLTIP}
                  id="approvedByConsensus"
                >
                  <Stack.Item className={commonStyles.flexHalf}>
                    <FormHookCheckBox
                      label="Approved by consensus?"
                      name={`isApprovedByConsensus`}
                      disabled={data?.isFlowUpdatable === false}
                    />
                  </Stack.Item>
                </TooltipHost>
                <TooltipHost
                  content={SINGLE_DECLINE_TOOLTIP}
                  id="singleDecline"
                >
                  <Stack.Item className={commonStyles.flexHalf}>
                    <FormHookCheckBox
                      label="Single Decline?"
                      name={`isSingleDeclineConsensusInvoked`}
                      disabled={data?.isFlowUpdatable === false}
                    />
                  </Stack.Item>
                </TooltipHost>
              </Stack>
            </Stack>

            <Stack tokens={{ childrenGap: 10 }}>
              <Stack horizontal tokens={{ childrenGap: 20 }}>
                <TooltipHost content={tooltipContent()}>
                  <Stack.Item className={commonStyles.flexHalf}>
                    <FormHookCheckBox
                      label="Additional Dept Level?"
                      name={`isUsedForAdditionalDepartments`}
                      disabled={data?.isFlowUpdatable === false}
                    />
                  </Stack.Item>
                </TooltipHost>
                <TooltipHost content={MINIMUM_APPROVERS_TOOLTIP}>
                  <Stack.Item className={commonStyles.flexHalf}>
                    <FormHookCheckBox
                      label="Require minimum Approver?"
                      name={`isRejectOnUnderApprovalCount`}
                      disabled={data?.isFlowUpdatable === false}
                    />
                  </Stack.Item>
                </TooltipHost>
              </Stack>
            </Stack>
          </Stack>

          {/* Footer */}
          <Stack
            horizontal
            style={{
              position: 'fixed',
              backgroundColor: `${theme.palette.neutralLighterAlt}`,
              width: '100%',
              zIndex: 10,
              bottom: 0,
            }}
            tokens={{ padding: '10px 20px 20px 20px', childrenGap: 10 }}
          >
            <DefaultButton
              text="Undo Changes"
              onClick={onClose}
              disabled={!isDirty && !isNew}
            />
            <Stack style={{ marginLeft: 'auto' }}>
              <DefaultButton
                text="Close"
                onClick={() => {
                  if (isDirty && isValid) {
                    handleSubmit(onHandleSave)();
                  } else onClose();
                }}
                disabled={!isValid && !isNew}
              />
            </Stack>
          </Stack>
        </Modal>
      )}
    </FormProvider>
  );
};
